import { useEffect, useRef } from 'react';
import type { Detection } from '@/utils/yoloPostProcess';
import { SURVEY_ITEM_ALIASES } from '@/constants/surveyItemAliases';

interface Props {
  // Ref rather than state value — the draw loop reads it directly each frame
  // so there are no React re-renders between clear and redraw.
  predictionsRef: React.MutableRefObject<Detection[]>;
  containerRef: React.RefObject<HTMLDivElement | null>;
  // Maps OIV7 class names to ItemDefinition display names; falls back to
  // SURVEY_ITEM_ALIASES then the raw OIV7 class name when absent.
  labelMap?: Map<string, string>;
}

function classColor(classIndex: number): string {
  const hue = (classIndex * 47) % 360;
  return `hsl(${hue}, 85%, 50%)`;
}

function drawFrame(
  canvas: HTMLCanvasElement,
  predictions: Detection[],
  containerRef: React.RefObject<HTMLDivElement | null>,
  labelMap?: Map<string, string>
) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const containerW = canvas.clientWidth;
  const containerH = canvas.clientHeight;

  if (canvas.width !== containerW || canvas.height !== containerH) {
    canvas.width = containerW;
    canvas.height = containerH;
  }

  ctx.clearRect(0, 0, containerW, containerH);

  if (!predictions.length) return;

  const video = containerRef.current?.querySelector('video');
  const videoW = video?.videoWidth || containerW;
  const videoH = video?.videoHeight || containerH;

  const scale = Math.min(containerW / videoW, containerH / videoH);
  const offsetX = (containerW - videoW * scale) / 2;
  const offsetY = (containerH - videoH * scale) / 2;

  for (const det of predictions) {
    const { x1, y1, x2, y2, classIndex, className, score } = det;
    const color = classColor(classIndex);
    const label = `${
      labelMap?.get(className) ?? SURVEY_ITEM_ALIASES[className] ?? className
    } ${Math.round(score * 100)}%`;

    const dx = offsetX + x1 * scale;
    const dy = offsetY + y1 * scale;
    const dw = (x2 - x1) * scale;
    const dh = (y2 - y1) * scale;

    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.strokeRect(dx, dy, dw, dh);

    ctx.font = 'bold 13px sans-serif';
    const textWidth = ctx.measureText(label).width;
    const labelX = dx;
    const labelY = dy > 20 ? dy - 4 : dy + dh + 16;

    ctx.fillStyle = color;
    ctx.fillRect(labelX - 1, labelY - 13, textWidth + 8, 17);

    ctx.fillStyle = '#fff';
    ctx.fillText(label, labelX + 3, labelY);
  }
}

export function ObjectDetectionOverlay({
  predictionsRef,
  containerRef,
  labelMap,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const labelMapRef = useRef(labelMap);
  labelMapRef.current = labelMap;

  useEffect(() => {
    let rafId: number;

    const loop = () => {
      if (canvasRef.current) {
        drawFrame(
          canvasRef.current,
          predictionsRef.current,
          containerRef,
          labelMapRef.current
        );
      }
      rafId = requestAnimationFrame(loop);
    };

    rafId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafId);
  }, [predictionsRef, containerRef]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 2,
      }}
    />
  );
}
